import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, TextField, Alert, CircularProgress, Switch, Grid,
  Tabs, Tab, Divider, Stack, Avatar, alpha, IconButton, Button, FormControl,
  Select, MenuItem,
} from '@mui/material';
import {
  Sms, Settings, Send, Refresh, CheckCircle, Message, Campaign,
  Notifications, PhoneAndroid,
} from '@mui/icons-material';
import { settingsAPI } from '../../api/client';
import { integerOnly } from '../../utils/inputHelpers';

function AdminSMSSettingsPage() {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [sms, setSms] = useState({
    enabled: 'true', provider: 'twilio', sender_id: 'CampusNest',
    booking_confirmation: 'true', payment_receipt: 'true',
    kyc_status: 'true', ticket_update: 'true',
    otp_enabled: 'true', otp_expiry_minutes: '5',
    daily_limit: '1000', rate_limit_per_user: '10',
    bulk_enabled: 'true',
    twilio_account_sid: '', twilio_auth_token: '', twilio_phone_number: '',
    hubtel_client_id: '', hubtel_client_secret: '',
    arkesel_api_key: '',
    mnotify_api_key: '',
  });

  useEffect(() => {
    setLoading(true);
    settingsAPI.list({ category: 'sms' }).then(res => {
      if (Array.isArray(res.data)) {
        setSettings(res.data);
        const m = {}; res.data.forEach(s => { m[s.key] = s.value; });
        setSms(p => ({ ...p, ...m }));
      }
    }).catch(() => setError('Failed to load SMS settings'))
    .finally(() => setLoading(false));
  }, []);

  const saveSetting = async (key, value) => {
    try {
      await settingsAPI.update(key, { value: String(value) });
      setSms(p => ({ ...p, [key]: String(value) }));
      setSuccess('Setting updated');
    } catch (_) { setError('Failed to update'); }
  };

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" fontWeight={800} sx={{ background: 'linear-gradient(135deg, #f59e0b, #fbbf24)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            SMS Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">Configure SMS gateway, templates, and bulk messaging</Typography>
        </Box>
        <IconButton onClick={() => window.location.reload()} sx={{ bgcolor: 'white', boxShadow: 1 }}><Refresh /></IconButton>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper sx={{ borderRadius: 3, mb: 3, px: 1 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ '& .MuiTabs-indicator': { height: 3, borderRadius: 2 } }}>
          <Tab label="Gateway Settings" icon={<PhoneAndroid sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Templates & Notifications" icon={<Message sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Bulk SMS" icon={<Campaign sx={{ fontSize: 18 }} />} iconPosition="start" />
        </Tabs>
      </Paper>

      {tabValue === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>SMS Gateway</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box><Typography fontWeight={600}>Enable SMS</Typography><Typography variant="body2" color="text.secondary">Master toggle for SMS notifications</Typography></Box>
                  <Switch checked={sms.enabled==='true'} onChange={(e)=>saveSetting('enabled', e.target.checked?'true':'false')} />
                </Stack>
                <Box><Typography fontWeight={600} gutterBottom>SMS Provider</Typography>
                  <FormControl size="small" fullWidth>
                    <Select value={sms.provider} onChange={(e) => saveSetting('provider', e.target.value)}>
                      <MenuItem value="twilio">Twilio</MenuItem>
                      <MenuItem value="hubtel">Hubtel</MenuItem>
                      <MenuItem value="arkesel">Arkesel</MenuItem>
                      <MenuItem value="mnotify">mNotify</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box><Typography fontWeight={600} gutterBottom>Sender ID</Typography>
                  <TextField size="small" fullWidth value={sms.sender_id}
                    onChange={(e)=>setSms(p=>({...p,sender_id:e.target.value}))}
                    onBlur={(e)=>saveSetting('sender_id', e.target.value)} />
                </Box>
              </Stack>
            </Paper>

            <Paper sx={{ p: 3, borderRadius: 3, mt: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>API Keys ({sms.provider})</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                {sms.provider === 'twilio' && (<>
                  <Box><Typography fontWeight={600} gutterBottom>Account SID</Typography>
                    <TextField size="small" fullWidth placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" value={sms.twilio_account_sid}
                      onChange={(e)=>setSms(p=>({...p,twilio_account_sid:e.target.value}))}
                      onBlur={(e)=>{ if(e.target.value) saveSetting('twilio_account_sid', e.target.value); }} />
                  </Box>
                  <Box><Typography fontWeight={600} gutterBottom>Auth Token</Typography>
                    <TextField size="small" fullWidth type="password" placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" value={sms.twilio_auth_token}
                      onChange={(e)=>setSms(p=>({...p,twilio_auth_token:e.target.value}))}
                      onBlur={(e)=>{ if(e.target.value) saveSetting('twilio_auth_token', e.target.value); }} />
                  </Box>
                  <Box><Typography fontWeight={600} gutterBottom>Phone Number</Typography>
                    <TextField size="small" fullWidth placeholder="+1234567890" value={sms.twilio_phone_number}
                      onChange={(e)=>setSms(p=>({...p,twilio_phone_number:e.target.value}))}
                      onBlur={(e)=>{ if(e.target.value) saveSetting('twilio_phone_number', e.target.value); }} />
                  </Box>
                </>)}
                {sms.provider === 'hubtel' && (<>
                  <Box><Typography fontWeight={600} gutterBottom>Client ID</Typography>
                    <TextField size="small" fullWidth placeholder="Enter Hubtel Client ID" value={sms.hubtel_client_id}
                      onChange={(e)=>setSms(p=>({...p,hubtel_client_id:e.target.value}))}
                      onBlur={(e)=>{ if(e.target.value) saveSetting('hubtel_client_id', e.target.value); }} />
                  </Box>
                  <Box><Typography fontWeight={600} gutterBottom>Client Secret</Typography>
                    <TextField size="small" fullWidth type="password" placeholder="Enter Hubtel Client Secret" value={sms.hubtel_client_secret}
                      onChange={(e)=>setSms(p=>({...p,hubtel_client_secret:e.target.value}))}
                      onBlur={(e)=>{ if(e.target.value) saveSetting('hubtel_client_secret', e.target.value); }} />
                  </Box>
                </>)}
                {sms.provider === 'arkesel' && (
                  <Box><Typography fontWeight={600} gutterBottom>API Key</Typography>
                    <TextField size="small" fullWidth type="password" placeholder="Enter Arkesel API Key" value={sms.arkesel_api_key}
                      onChange={(e)=>setSms(p=>({...p,arkesel_api_key:e.target.value}))}
                      onBlur={(e)=>{ if(e.target.value) saveSetting('arkesel_api_key', e.target.value); }} />
                  </Box>
                )}
                {sms.provider === 'mnotify' && (
                  <Box><Typography fontWeight={600} gutterBottom>API Key</Typography>
                    <TextField size="small" fullWidth type="password" placeholder="Enter mNotify API Key" value={sms.mnotify_api_key}
                      onChange={(e)=>setSms(p=>({...p,mnotify_api_key:e.target.value}))}
                      onBlur={(e)=>{ if(e.target.value) saveSetting('mnotify_api_key', e.target.value); }} />
                  </Box>
                )}
                <Typography variant="caption" color="text.secondary">
                  API keys are stored encrypted and never exposed in API responses.
                </Typography>
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>OTP & Limits</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box><Typography fontWeight={600}>OTP Verification</Typography><Typography variant="body2" color="text.secondary">Send OTP codes via SMS</Typography></Box>
                  <Switch checked={sms.otp_enabled==='true'} onChange={(e)=>saveSetting('otp_enabled', e.target.checked?'true':'false')} />
                </Stack>
                {[
                  { key: 'otp_expiry_minutes', label: 'OTP Expiry (minutes)' },
                  { key: 'daily_limit', label: 'Daily SMS Limit' },
                  { key: 'rate_limit_per_user', label: 'Rate Limit per User (per hour)' },
                ].map(item => (
                  <Box key={item.key}><Typography fontWeight={600} gutterBottom>{item.label}</Typography>
                    <TextField size="small" type="number" value={sms[item.key]}
                      onChange={(e)=>setSms(p=>({...p,[item.key]:e.target.value}))}
                      onKeyDown={integerOnly}
                      onBlur={(e)=>saveSetting(item.key, e.target.value)} sx={{ width: 140 }} />
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}

      {tabValue === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Notification Triggers</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                {[
                  { key: 'booking_confirmation', label: 'Booking Confirmation', desc: 'When a booking is confirmed' },
                  { key: 'payment_receipt', label: 'Payment Receipt', desc: 'When payment is received' },
                  { key: 'kyc_status', label: 'KYC Status Update', desc: 'When KYC is approved/rejected' },
                  { key: 'ticket_update', label: 'Ticket Updates', desc: 'When support ticket status changes' },
                ].map(item => (
                  <Paper key={item.key} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box><Typography fontWeight={600}>{item.label}</Typography><Typography variant="caption" color="text.secondary">{item.desc}</Typography></Box>
                      <Switch checked={sms[item.key]==='true'} onChange={(e)=>saveSetting(item.key, e.target.checked?'true':'false')} />
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Message Templates</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={1.5}>
                {[
                  { name: 'Booking Confirmed', preview: 'Hi {name}, your booking #{id} has been confirmed.', color: '#10b981' },
                  { name: 'Payment Received', preview: 'Payment of GH₵{amount} received. Ref: {ref}', color: '#3b82f6' },
                  { name: 'KYC Approved', preview: 'Your identity verification has been approved.', color: '#6366f1' },
                  { name: 'OTP Code', preview: 'Your CampusNest OTP is {code}. Valid for 5 mins.', color: '#f59e0b' },
                  { name: 'Rent Reminder', preview: 'Rent of GH₵{amount} due on {date}.', color: '#ef4444' },
                ].map((t, i) => (
                  <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: 2, borderLeftWidth: 4, borderLeftColor: t.color }}>
                    <Typography fontWeight={600}>{t.name}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>{t.preview}</Typography>
                  </Paper>
                ))}
                <Button variant="outlined" fullWidth>Edit Templates</Button>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}

      {tabValue === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Bulk SMS</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box><Typography fontWeight={600}>Enable Bulk SMS</Typography><Typography variant="body2" color="text.secondary">Send mass notifications</Typography></Box>
                  <Switch checked={sms.bulk_enabled==='true'} onChange={(e)=>saveSetting('bulk_enabled', e.target.checked?'true':'false')} />
                </Stack>
                <Box><Typography fontWeight={600} gutterBottom>Target Audience</Typography>
                  <FormControl size="small" fullWidth>
                    <Select defaultValue="all">
                      <MenuItem value="all">All Users</MenuItem>
                      <MenuItem value="tenants">Tenants Only</MenuItem>
                      <MenuItem value="landlords">Landlords Only</MenuItem>
                      <MenuItem value="agents">Agents Only</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box><Typography fontWeight={600} gutterBottom>Message</Typography>
                  <TextField fullWidth multiline rows={4} placeholder="Type your bulk SMS message here..." />
                </Box>
                <Button variant="contained" startIcon={<Send />} fullWidth>Send Bulk SMS</Button>
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>SMS Statistics</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                {[
                  { label: 'Sent Today', value: '342', color: '#6366f1' },
                  { label: 'Delivered', value: '338', color: '#10b981' },
                  { label: 'Failed', value: '4', color: '#ef4444' },
                  { label: 'Credits Remaining', value: '8,567', color: '#f59e0b' },
                ].map((stat, i) => (
                  <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography fontWeight={600}>{stat.label}</Typography>
                      <Typography variant="h6" fontWeight={800} color={stat.color}>{stat.value}</Typography>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

export default AdminSMSSettingsPage;
